import { useState, useEffect, useCallback } from "react";
import { api, apiBase, clearJwt } from "./api";
import Topbar from "./shell/Topbar";
import Subnav, { type Tab } from "./shell/Subnav";
import Toast from "./components/Toast";
import Dashboard from "./screens/Dashboard";
import Catalog from "./screens/Catalog";
import AssetDetail from "./screens/AssetDetail";
import ChangeRequests from "./screens/ChangeRequests";
import MyRequests from "./screens/MyRequests";
import History from "./screens/History";
import Settings from "./screens/Settings";
import SignIn from "./screens/SignIn";
import CommandPalette from "./screens/CommandPalette";
import WelcomeBanner from "./components/WelcomeBanner";
import type { ClientRow, Identity } from "./types";

export default function App() {
  const [identity, setIdentity] = useState<Identity | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [clientId, setClientId] = useState<string | null>(
    () => localStorage.getItem("bgspim_client_id")
  );
  const [clients, setClients] = useState<ClientRow[]>([]);
  const [tab, setTab] = useState<Tab>("catalog");
  const [openAsset, setOpenAsset] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [cpOpen, setCpOpen] = useState(false);

  const client = clientId ? (clients.find((c) => c.id === clientId) ?? null) : null;

  const loadClients = useCallback(async () => {
    if (!apiBase()) return;
    try {
      const data = await api.getClients();
      setClients(data.clients);
    } catch {
      // silent — Dashboard shows the error
    }
  }, []);

  useEffect(() => {
    api
      .getMe()
      .then((res) => {
        setIdentity(res.identity);
        if (res.identity.role !== "platform_owner" && res.identity.client_id) {
          setClientId(res.identity.client_id);
        }
      })
      .catch(() => {
        clearJwt();
      })
      .finally(() => setAuthLoading(false));
  }, []);

  useEffect(() => {
    if (identity) void loadClients();
  }, [identity, loadClients]);

  useEffect(() => {
    if (clientId) localStorage.setItem("bgspim_client_id", clientId);
    else localStorage.removeItem("bgspim_client_id");
  }, [clientId]);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2200);
  }, []);

  // Global hotkeys
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement)?.tagName;
      const isText = tag === "INPUT" || tag === "TEXTAREA";
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setCpOpen(true);
      }
      if (e.key === "Escape") {
        if (cpOpen) setCpOpen(false);
        else if (openAsset) setOpenAsset(null);
      }
      if (!isText && (e.metaKey || e.ctrlKey) && /^[1-4]$/.test(e.key)) {
        e.preventDefault();
        const map: Record<string, Tab> = { "1": "catalog", "2": "requests", "3": "history", "4": "settings" };
        setTab(map[e.key]);
        setOpenAsset(null);
      }
    }
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [cpOpen, openAsset]);

  function handleAuth(newIdentity: Identity) {
    setIdentity(newIdentity);
    if (newIdentity.role !== "platform_owner" && newIdentity.client_id) {
      setClientId(newIdentity.client_id);
    }
  }

  function handleSignOut() {
    clearJwt();
    localStorage.removeItem("bgspim_client_id");
    setIdentity(null);
    setClientId(null);
    setTab("catalog");
    setOpenAsset(null);
  }

  function pickClient(id: string) {
    setClientId(id);
    setTab("catalog");
    setOpenAsset(null);
  }

  function switchClient() {
    setClientId(null);
    setTab("catalog");
    setOpenAsset(null);
  }

  if (authLoading) {
    return <div className="app" style={{ minHeight: "100vh", background: "var(--bg)" }} />;
  }

  if (!identity) {
    return <SignIn onAuth={handleAuth} />;
  }

  // Dashboard: no client selected. Only platform_owner reaches this branch;
  // client roles are pinned to identity.client_id by handleAuth / getMe, so a
  // null clientId for a non-platform_owner identity is a data error.
  if (!clientId) {
    if (identity.role !== "platform_owner") {
      return (
        <div className="app">
          <Topbar
            identity={identity}
            onSignOut={handleSignOut}
            client={null}
            canSwitchClient={false}
            onSwitchClient={() => {}}
            onOpenSearch={() => {}}
          />
          <main style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
            <div className="empty card" style={{ padding: "60px 32px", maxWidth: 480 }}>
              <h4 className="t-h4">No workspace assigned.</h4>
              <p style={{ marginTop: 8 }}>
                Your account isn't connected to a client yet. Reach out to Mark and he'll get you set up.
              </p>
            </div>
          </main>
        </div>
      );
    }
    return (
      <div className="app">
        <Topbar
          identity={identity}
          onSignOut={handleSignOut}
          client={null}
          canSwitchClient={false}
          onSwitchClient={() => {}}
          onOpenSearch={() => setCpOpen(true)}
        />
        <main style={{ flex: 1 }}>
          <Dashboard onPickClient={pickClient} />
        </main>
        {toast && <Toast>{toast}</Toast>}
      </div>
    );
  }

  // Workspace: client selected
  return (
    <div className="app">
      <Topbar
        identity={identity}
        onSignOut={handleSignOut}
        client={client}
        canSwitchClient={identity.role === "platform_owner"}
        onSwitchClient={switchClient}
        onOpenSearch={() => setCpOpen(true)}
      />
      <Subnav
        tab={tab}
        onSetTab={(t) => { setTab(t); setOpenAsset(null); }}
        client={client}
        identity={identity}
      />
      <main style={{ flex: 1 }}>
        {tab === "catalog" && !openAsset && (
          <>
            {client && identity.role !== "platform_owner" && (
              <div style={{ maxWidth: 1480, margin: "0 auto", padding: "20px 24px 0" }}>
                <WelcomeBanner identity={identity} client={client} />
              </div>
            )}
            <Catalog
              clientId={clientId}
              identity={identity}
              onOpenAsset={setOpenAsset}
              onNewAsset={() => setOpenAsset("new")}
            />
          </>
        )}
        {tab === "catalog" && openAsset && (
          <AssetDetail
            clientId={clientId}
            clientRow={client}
            identity={identity}
            assetId={openAsset}
            onBack={() => setOpenAsset(null)}
            onShowToast={showToast}
          />
        )}
        {tab === "requests" && identity.role === "platform_owner" && (
          <ChangeRequests identity={identity} onShowToast={showToast} />
        )}
        {tab === "requests" && identity.role !== "platform_owner" && (
          <MyRequests
            identity={identity}
            onShowToast={showToast}
            onOpenAsset={(id) => { setOpenAsset(id); setTab("catalog"); }}
          />
        )}
        {tab === "history"  && <History clientId={clientId} />}
        {tab === "settings" && <Settings client={client} identity={identity} />}
      </main>
      {cpOpen && (
        <CommandPalette
          clientId={clientId}
          clientName={client?.name ?? null}
          canSwitchClient={identity.role === "platform_owner"}
          onClose={() => setCpOpen(false)}
          onNavigate={(t) => { setTab(t); setOpenAsset(null); setCpOpen(false); }}
          onOpenAsset={(id) => { setOpenAsset(id); setTab("catalog"); setCpOpen(false); }}
          onNewAsset={() => { setOpenAsset("new"); setTab("catalog"); setCpOpen(false); }}
          onSwitchClient={() => { switchClient(); setCpOpen(false); }}
        />
      )}
      {toast && <Toast>{toast}</Toast>}
    </div>
  );
}
